import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ArrowLeft, Users, BookOpen, CheckCircle, Clock } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { assignmentsApi, Assignment, AssignmentQuestion, AssignmentStudent } from '../../api/assignments'
import { rosterApi, Roster } from '../../api/roster'

const STATUS_COLOR: Record<string, string> = {
  unassigned: 'gray',
  assigned: 'blue',
  processing: 'grape',
  submitted: 'orange',
  graded: 'green',
  reviewed: 'green',
}

function formatDate(d: string | null) {
  if (!d) return '—'
  return new Date(d).toLocaleString()
}

function isReviewedStatus(status: string) {
  return status === 'graded' || status === 'reviewed'
}

export function AssignmentDetail() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const isTutor = user?.role === 'tutor' || user?.role === 'god'

  const [assignment, setAssignment] = useState<Assignment | null>(null)
  const [questions, setQuestions] = useState<AssignmentQuestion[]>([])
  const [students, setStudents] = useState<AssignmentStudent[]>([])
  const [loading, setLoading] = useState(true)

  const [assignOpen, setAssignOpen] = useState(false)
  const [rosters, setRosters] = useState<Roster[]>([])
  const [selectedRosterId, setSelectedRosterId] = useState<string | null>(null)
  const [assignAll, setAssignAll] = useState(true)
  const [rosterStudents, setRosterStudents] = useState<AssignmentStudent[]>([])
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([])
  const [assigning, setAssigning] = useState(false)
  const [loadingRosterStudents, setLoadingRosterStudents] = useState(false)

  const [myStatus, setMyStatus] = useState<AssignmentStudent | null>(null)

  const load = () => {
    assignmentsApi.get(Number(id))
      .then((res) => {
        setAssignment(res.data.assignment)
        setQuestions(res.data.questions)
        setStudents(res.data.students)
        const mine = res.data.students.find((s) => s.student_id === user?.id)
        setMyStatus(mine || null)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load assignment', color: 'red' }))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    if (isTutor) rosterApi.list().then((res) => setRosters(res.data))
  }, [id, isTutor, user?.id])

  const submittedRatio = useMemo(() => {
    if (!assignment || assignment.total_assigned_students === 0) {
      return '0/0'
    }
    return `${assignment.submitted_students_count}/${assignment.total_assigned_students}`
  }, [assignment])

  const canViewStudentResults = !!assignment && !!myStatus && (
    (assignment.grading_mode === 'auto' && ['submitted', 'graded', 'reviewed'].includes(myStatus.status)) ||
    isReviewedStatus(myStatus.status)
  )

  const handleRosterChange = async (value: string | null) => {
    setSelectedRosterId(value)
    setSelectedStudentIds([])
    setAssignAll(true)
    setRosterStudents([])

    if (!value) {
      return
    }

    setLoadingRosterStudents(true)
    try {
      const res = await rosterApi.get(Number(value))
      setRosterStudents(
        res.data.students.map((student) => ({
          id: student.id,
          assignment_id: Number(id),
          student_id: student.student_id,
          student_name: student.student_name,
          student_email: student.student_email,
          status: 'assigned',
          started_at: null,
          submitted_at: null,
          graded_at: null,
          score: null,
          xp_earned: 0,
          tutor_feedback: null,
        }))
      )
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load roster students.', color: 'red' })
    } finally {
      setLoadingRosterStudents(false)
    }
  }

  const handleAssign = async () => {
    if (!selectedRosterId) {
      return
    }

    setAssigning(true)
    try {
      await assignmentsApi.assign(Number(id), assignAll
        ? { roster_id: Number(selectedRosterId) }
        : { student_ids: selectedStudentIds.map(Number) }
      )
      notifications.show({ title: 'Assigned', message: 'Assignment sent successfully.', color: 'green' })
      setAssignOpen(false)
      setSelectedRosterId(null)
      setSelectedStudentIds([])
      setRosterStudents([])
      load()
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed', color: 'red' })
    } finally {
      setAssigning(false)
    }
  }

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  if (!assignment) {
    return (
      <Container py={32}>
        <Alert color="red">Assignment not found.</Alert>
      </Container>
    )
  }

  return (
    <Container size="md" py={32}>
      <Group mb={24}>
        <ActionIcon variant="subtle" color="gray" onClick={() => navigate(isTutor ? '/tutor/assignments' : '/assignments')}>
          <ArrowLeft size={20} />
        </ActionIcon>
        <Box style={{ flex: 1 }}>
          <Group gap={8}>
            <Title order={2} c="var(--sat-text)">{assignment.title}</Title>
            <Badge color="blue" variant="light">{assignment.assignment_type}</Badge>
            <Badge color={assignment.grading_mode === 'auto' ? 'green' : 'violet'} variant="light">
              {assignment.grading_mode === 'auto' ? 'Auto-graded' : 'Manual-graded'}
            </Badge>
          </Group>
          {assignment.description && <Text size="sm" c="var(--sat-text-soft)" mt={2}>{assignment.description}</Text>}
        </Box>
        {isTutor && (
          <Button
            leftSection={<Users size={16} />}
            variant="outline"
            color="eveningSea"
            size="sm"
            onClick={() => setAssignOpen(true)}
          >
            Assign Students
          </Button>
        )}
      </Group>

      {/* Details */}
      <Paper shadow="xs" radius="md" p="md" mb={20} style={{ background: 'var(--sat-bg)' }}>
        <Group gap={32} wrap="wrap">
          <Box>
            <Text size="xs" c="var(--sat-text-faint)">Questions</Text>
            <Text fw={700} c="var(--sat-text)">{questions.length}</Text>
          </Box>
          <Box>
            <Text size="xs" c="var(--sat-text-faint)">Base XP</Text>
            <Text fw={700} c="var(--sat-yellow)">{assignment.base_xp}</Text>
          </Box>
          <Box>
            <Text size="xs" c="var(--sat-text-faint)">Status</Text>
            <Badge color={STATUS_COLOR[assignment.status] || 'gray'} variant="light">{assignment.status}</Badge>
          </Box>
          {isTutor && (
            <Box>
              <Text size="xs" c="var(--sat-text-faint)">Submitted Ratio</Text>
              <Text fw={700} c="var(--sat-text)">{submittedRatio}</Text>
            </Box>
          )}
          <Box>
            <Text size="xs" c="var(--sat-text-faint)">Due Date</Text>
            <Text fw={600} c="var(--sat-text)">
              {assignment.due_date ? new Date(assignment.due_date).toLocaleDateString() : 'No deadline'}
            </Text>
          </Box>
          {!isTutor && myStatus && (
            <Box>
              <Text size="xs" c="var(--sat-text-faint)">Your Status</Text>
              <Badge color={STATUS_COLOR[myStatus.status] || 'gray'}>{myStatus.status}</Badge>
            </Box>
          )}
        </Group>
      </Paper>

      {/* Student action */}
      {!isTutor && myStatus && (
        <Paper shadow="sm" radius="md" p="lg" mb={20}>
          <Group justify="space-between">
            <Box>
              <Text fw={600} c="var(--sat-text)">
                {isReviewedStatus(myStatus.status) ? 'Assignment reviewed'
                  : myStatus.status === 'submitted'
                    ? (assignment.grading_mode === 'auto' ? 'Results ready' : 'Awaiting tutor grading')
                    : myStatus.status === 'processing' ? 'In progress'
                    : 'Ready to start'}
              </Text>
              {(isReviewedStatus(myStatus.status) || (assignment.grading_mode === 'auto' && myStatus.status === 'submitted')) && (
                <Group gap={16} mt={4}>
                  <Text size="sm" c="var(--sat-green)">Score: <strong>{myStatus.score !== null ? `${Math.round(myStatus.score)}%` : '—'}</strong></Text>
                  {isReviewedStatus(myStatus.status) && <Text size="sm" c="var(--sat-yellow)">XP: <strong>+{myStatus.xp_earned}</strong></Text>}
                </Group>
              )}
              {myStatus.tutor_feedback && (
                <Text size="sm" c="var(--sat-text-subtle)" mt={4} style={{ fontStyle: 'italic' }}>"{myStatus.tutor_feedback}"</Text>
              )}
              {myStatus.submitted_at && (
                <Text size="xs" c="var(--sat-text-faint)" mt={4}>Submitted: {formatDate(myStatus.submitted_at)}</Text>
              )}
            </Box>
            {(myStatus.status === 'assigned' || myStatus.status === 'processing') && (
              <Button
                color="grapefruit"
                leftSection={<BookOpen size={16} />}
                onClick={() => navigate(`/assignments/${id}/practice`)}
              >
                {myStatus.status === 'processing' ? 'Continue' : 'Start Assignment'}
              </Button>
            )}
            {canViewStudentResults && (
              <Button
                color="eveningSea"
                variant="light"
                leftSection={<CheckCircle size={16} />}
                onClick={() => navigate(`/assignments/${id}/practice`)}
              >
                View Results
              </Button>
            )}
            {isReviewedStatus(myStatus.status) && <CheckCircle size={32} color="var(--sat-green)" />}
            {myStatus.status === 'submitted' && <Clock size={32} color="var(--sat-yellow)" />}
          </Group>
        </Paper>
      )}

      {!isTutor && !myStatus && (
        <Alert color="yellow" mb={20}>This assignment hasn't been assigned to you yet.</Alert>
      )}

      {/* Questions preview */}
      <Paper shadow="xs" radius="md" p={0} mb={20} style={{ overflow: 'hidden' }}>
        <Group px={20} py={12} style={{ background: 'var(--sat-bg)', borderBottom: '1px solid var(--sat-border-soft)' }}>
          <BookOpen size={16} color="var(--sat-green)" />
          <Text fw={600} size="sm" c="var(--sat-text)">{questions.length} Questions</Text>
        </Group>
        {questions.map((q, i) => (
          <Box key={q.id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--sat-border-faint)' }}>
            <Stack gap={4}>
              <Group gap={8}>
                <Text size="xs" c="var(--sat-text-faint)" style={{ width: 24 }}>{i + 1}.</Text>
                <Text size="sm" c="var(--sat-text-subtle)" style={{ flex: 1 }}>
                  {q.prompt.slice(0, 140)}{q.prompt.length > 140 ? '…' : ''}
                </Text>
                <Badge size="xs" color="gray">D{q.difficulty_level}</Badge>
              </Group>
              {q.passage_text && (
                <Text size="xs" c="var(--sat-text-faint)" ml={32}>
                  Passage: {q.passage_text.slice(0, 120)}{q.passage_text.length > 120 ? '…' : ''}
                </Text>
              )}
            </Stack>
          </Box>
        ))}
      </Paper>

      {/* Tutor student table */}
      {isTutor && (
        <Paper shadow="xs" radius="md" p={0} style={{ overflow: 'hidden' }}>
          <Group px={20} py={12} style={{ background: 'var(--sat-bg)', borderBottom: '1px solid var(--sat-border-soft)' }}>
            <Users size={16} color="var(--sat-green)" />
            <Text fw={600} size="sm" c="var(--sat-text)">{students.length} Students</Text>
          </Group>
          {students.length === 0 ? (
            <Text c="var(--sat-text-faint)" size="sm" p={20}>No students assigned. Use "Assign Students" above.</Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Student</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Submitted</Table.Th>
                  <Table.Th>Score</Table.Th>
                  <Table.Th>Feedback</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {students.map((s) => (
                  <Table.Tr key={s.id}>
                    <Table.Td><Text size="sm" fw={500}>{s.student_name || `Student #${s.student_id}`}</Text></Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={STATUS_COLOR[s.status] || 'gray'} variant="light">{s.status}</Badge>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="var(--sat-text-faint)">{formatDate(s.submitted_at)}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm" c="var(--sat-green)" fw={600}>{s.score !== null ? `${Math.round(s.score)}%` : '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="var(--sat-text-faint)">{s.tutor_feedback || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {(s.status === 'submitted' || isReviewedStatus(s.status)) && (
                        <Button
                          size="xs"
                          color="eveningSea"
                          onClick={() => navigate(`/tutor/grading?assignment=${id}&student=${s.student_id}`)}
                        >
                          {s.status === 'submitted' ? 'Open Result' : 'View Grade'}
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      )}

      <Modal
        opened={assignOpen}
        onClose={() => setAssignOpen(false)}
        title={<Text fw={700} c="var(--sat-text)">Assign Students</Text>}
        radius="md"
      >
        <Stack gap={16}>
          {rosters.length === 0 ? (
            <Alert color="yellow">No rosters found.</Alert>
          ) : (
            <>
              <Select
                label="Select roster"
                data={rosters.map((r) => ({ value: String(r.id), label: r.name }))}
                value={selectedRosterId}
                onChange={handleRosterChange}
                placeholder="Choose a roster"
              />
              {selectedRosterId && (
                <>
                  <Switch
                    checked={assignAll}
                    onChange={(event) => setAssignAll(event.currentTarget.checked)}
                    label="Assign to whole roster"
                  />
                  {!assignAll && (
                    <MultiSelect
                      label="Select students"
                      placeholder={loadingRosterStudents ? 'Loading students...' : 'Choose students'}
                      data={rosterStudents.map((student) => ({
                        value: String(student.student_id),
                        label: student.student_name || student.student_email || `Student #${student.student_id}`,
                      }))}
                      value={selectedStudentIds}
                      onChange={setSelectedStudentIds}
                      searchable
                    />
                  )}
                </>
              )}
            </>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button
              color="grapefruit"
              disabled={!selectedRosterId || (!assignAll && selectedStudentIds.length === 0)}
              loading={assigning}
              onClick={handleAssign}
            >
              Assign
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  )
}
